import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChartLineUp, CaretLeft, EnvelopeOpen, CursorClick, ArrowsClockwise } from "@phosphor-icons/react";
import { api } from "../context/AuthContext";
import { Button } from "../components/ui/button";

const fmtDate = (iso) => iso ? new Date(iso).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : "—";

export default function MarketingStatsPage() {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/marketing/newsletter/stats")
      .then(({ data }) => setRows(data.newsletters))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  return (
    <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="marketing-stats-page">
      <div className="mb-10 animate-fade-up flex flex-wrap items-end justify-between gap-4">
        <div>
          <Link to="/marketing/email" data-testid="back-to-email" className="inline-flex items-center gap-1 text-xs font-semibold text-white/50 hover:text-white transition-colors mb-4">
            <CaretLeft size={13} /> Email
          </Link>
          <p className="text-xs tracking-[0.3em] uppercase text-white/40 mb-2 flex items-center gap-1.5">
            <ChartLineUp size={13} className="text-emerald-400" /> Rendimento newsletter
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tighter leading-none">Statistiche</h1>
          <p className="text-sm text-slate-400 mt-3 max-w-2xl">
            Aperture e clic delle newsletter inviate. Il tracciamento vale per le email HTML inviate da ora in poi (pixel di apertura + link tracciati).
          </p>
        </div>
        <Button data-testid="stats-reload-button" onClick={load} disabled={loading} variant="ghost"
          className="h-10 px-5 border border-white/20 text-white hover:bg-white/5 hover:text-white rounded-full text-xs font-semibold">
          <ArrowsClockwise size={14} className={`mr-1.5 ${loading ? "animate-spin" : ""}`} /> Aggiorna
        </Button>
      </div>

      <div className="p-8 rounded-lg bg-surface border border-surface-border animate-fade-up" data-testid="stats-card">
        {rows === null && <p className="text-xs text-white/40 text-center py-6">Caricamento...</p>}
        {rows && rows.length === 0 && (
          <p className="text-sm text-white/40 text-center py-6" data-testid="stats-empty">
            Nessuna newsletter inviata finora. Le statistiche appariranno qui dopo il primo invio.
          </p>
        )}
        {rows && rows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="stats-table">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-white/30 text-left">
                  <th className="py-2 pr-3 font-medium">Newsletter</th>
                  <th className="py-2 pr-3 font-medium">Data</th>
                  <th className="py-2 pr-3 font-medium text-right">Inviate</th>
                  <th className="py-2 pr-3 font-medium text-right">Aperture</th>
                  <th className="py-2 pr-3 font-medium text-right">Clic</th>
                  <th className="py-2 font-medium text-right">Link</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <>
                    <tr key={r.id} className="border-t border-white/5 text-white/75" data-testid={`stats-row-${r.id}`}>
                      <td className="py-3 pr-3 font-semibold truncate max-w-[220px]" title={r.subject}>
                        {r.subject}
                        {!r.ok && <span className="ml-2 text-[9px] uppercase font-semibold text-red-400">fallita</span>}
                        {r.ok && !r.tracked && <span className="ml-2 text-[9px] uppercase text-white/30" title="Inviata senza HTML o prima dell'attivazione del tracciamento">non tracciata</span>}
                      </td>
                      <td className="py-3 pr-3 text-white/45 whitespace-nowrap">{fmtDate(r.timestamp)}</td>
                      <td className="py-3 pr-3 text-right">{r.sent}</td>
                      <td className="py-3 pr-3 text-right">
                        <span className="inline-flex items-center gap-1.5 font-semibold text-sky-300">
                          <EnvelopeOpen size={12} /> {r.opens}
                          <span className="text-white/35 font-normal">({r.open_rate}%)</span>
                        </span>
                      </td>
                      <td className="py-3 pr-3 text-right">
                        <span className="inline-flex items-center gap-1.5 font-semibold text-emerald-300">
                          <CursorClick size={12} /> {r.clicks}
                          <span className="text-white/35 font-normal">({r.click_rate}%)</span>
                        </span>
                      </td>
                      <td className="py-3 text-right">
                        {r.top_links.length > 0 ? (
                          <button type="button" data-testid={`stats-links-toggle-${r.id}`}
                            onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                            className="text-[11px] font-semibold text-white/50 hover:text-white underline underline-offset-2">
                            {expanded === r.id ? "Nascondi" : `${r.top_links.length} link`}
                          </button>
                        ) : <span className="text-white/25">—</span>}
                      </td>
                    </tr>
                    {expanded === r.id && (
                      <tr key={`${r.id}-links`} className="border-t border-white/5">
                        <td colSpan={6} className="py-2 pl-4">
                          {r.top_links.map((l) => (
                            <p key={l.url} className="text-[11px] text-white/50 py-0.5">
                              <span className="text-emerald-300 font-semibold mr-2">{l.clicks} clic</span>
                              <span className="break-all">{l.url}</span>
                            </p>
                          ))}
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="text-[10px] text-white/30 mt-4">
          Aperture = destinatari unici che hanno aperto (alcuni client email bloccano le immagini: il dato reale può essere più alto). Clic = destinatari unici che hanno cliccato almeno un link.
        </p>
      </div>
    </main>
  );
}
